import { toast } from '@approvalmax/ui/src/components';
import { MessageResultValue } from '@approvalmax/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSetAtom } from 'jotai';
import { backend, domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { amplitudeService } from 'services/amplitude';
import { notificationService } from 'services/notification';
import { routingService } from 'services/routing';
import { usePublishRequest } from 'shared/data/webApp/v1';
import { hasChangesState } from 'shared/states';
import { getPath, Path } from 'urlBuilder';
import { z } from 'zod';

import { completeReview } from '../../../actions';
import { useEditRequestForm } from '../../../hooks/useEditRequestForm';
import { getEditCommands } from '../../../selectors/requestSelectors';
import { isModifiedRequestState } from '../../../states';
import { mapRequestToForm } from './XeroContactRequestForm.helpers';
import { messages } from './XeroContactRequestForm.messages';
import { schema } from './XeroContactRequestForm.schema';

export const useRequestForm = (requestDetails: domain.XeroContactRequest['details'], isUKCompany: boolean) => {
    const setHasChanges = useSetAtom(hasChangesState);

    const form = useForm<z.infer<typeof schema>>({
        values: mapRequestToForm(requestDetails, isUKCompany),
        resolver: zodResolver(schema),
    });

    useEffect(() => {
        if (form.formState.isDirty) {
            setHasChanges(true);
        }
    }, [form.formState.isDirty, setHasChanges]);

    const handleFormSubmit = useCallback(async () => {
        /**
         * We use form submit here for form validation
         * We need to submit form for validation to work as expected with live error highlight
         * We could use `trigger` method but there will be no live error highlight
         * If form data is valid we return true in success callback
         * If form has validation error we return false in errors callback
         */
        return new Promise<boolean>((resolve) => {
            form.handleSubmit(
                () => {
                    resolve(true);
                },
                () => {
                    resolve(false);
                }
            )();
        });
    }, [form]);

    const handleFormValidation = useCallback(async () => {
        const isValid = await handleFormSubmit();

        if (!isValid) {
            toast.error(messages.validationError);
        }

        return isValid ? [] : [messages.validationError];
    }, [handleFormSubmit]);

    return {
        form,
        handleFormValidation,
    };
};

export const useSaveAndSubmitContact = (options: {
    requestId: string;
    companyId: string;
    getRequestTransfer: () => backend.Transfer<backend.transfers.RequestEditTransfer>;
    validateForm: () => Promise<MessageResultValue[]>;
}) => {
    const { requestId, companyId, getRequestTransfer, validateForm } = options;

    const commands = useSelector(getEditCommands);
    const setIsModifiedRequest = useSetAtom(isModifiedRequestState);
    const setHasChanges = useSetAtom(hasChangesState);

    const [isSaving, setIsSaving] = useState(false);

    const { mutateAsync: publishRequest } = usePublishRequest();
    const { mutateAsync: editRequest } = useEditRequestForm();

    const saveAndSubmitContact = useCallback(
        async (commentText: string, needToCompleteReview: boolean) => {
            const errors = await validateForm();

            if (errors.length > 0) {
                return;
            }

            const transfer = getRequestTransfer();

            setIsModifiedRequest(true);

            setHasChanges(false);

            setIsSaving(true);

            try {
                await editRequest({
                    body: {
                        ...transfer,
                        commentText,
                    },
                });

                if (!commands.saveAndSubmit.hidden && !commands.saveAndSubmit.disabled) {
                    await publishRequest({
                        body: { requestId, companyId },
                    });

                    amplitudeService.sendData('requests: create request', {
                        'request type': domain.IntegrationCode.XeroContact.toLocaleLowerCase(),
                    });
                }

                if (needToCompleteReview) {
                    await completeReview(requestId, companyId);
                }

                notificationService.showInfoToast(messages.saveAndSubmitRequestSuccess);
                setIsSaving(false);

                routingService.push(getPath(Path.request, requestId, companyId));
            } catch {
                setIsSaving(false);
            }
        },
        [
            validateForm,
            getRequestTransfer,
            setIsModifiedRequest,
            editRequest,
            commands.saveAndSubmit.hidden,
            commands.saveAndSubmit.disabled,
            setHasChanges,
            requestId,
            companyId,
            publishRequest,
        ]
    );

    return { saveAndSubmitContact, isSaving };
};
